import { fetchJSON as fetchAPI } from './client';

export type BusinessObjective = { key: string; name: string; description: string; kpi?: string };

export type WorkspacePhilosophyQuestion = { key: string; question: string; answered_by?: string };

export type LayoutRegion = { key: string; name: string; order: number; region: string; component?: string; description?: string };

export type OpportunityInboxItem = {
  key: string; title: string; business_problem: string; expected_revenue: string; expected_profit: string;
  confidence: number; estimated_effort: string; required_time: string; risk: string; one_click_action: string;
  evidence?: string[]; observation_window?: string; dependencies?: string[]; priority_score: number; category?: string;
};

export type ExecutiveCard = {
  key: string; name: string; metric?: string; unit?: string; actionable: boolean;
  drill_down?: string; alert_threshold?: string; kpis?: string[];
};

export type AIWorkspaceSection = { key: string; name: string; description: string; persistent: boolean };

export type QuickAction = { key: string; name: string; command: string; shortcut?: string; permission?: string; target_latency?: string };

export type LiveUpdateChannel = { key: string; name: string; websocket: string; target_latency: string; event_topic?: string };

export type PerformanceTarget = { key: string; surface: string; max_value: string; unit: string; description?: string };

export type APISpecification = {
  key: string; category: string; endpoints?: string[]; description: string;
  authorization?: string; rate_limit?: string; cache_strategy?: string;
};

export type DatabaseTable = {
  key: string; name: string; purpose: string; columns?: string[]; indexes?: string[]; relationships?: string[];
};

export type AICapability = { key: string; name: string; description: string; dna_usage: boolean };

export type TestingRequirement = { key: string; level: string; scope: string; required: boolean; description?: string };

export type AcceptanceCriterion = { key: string; criterion: string; measurable: boolean; target?: string };

export type CommandCenterValidation = {
  valid: boolean; layout_complete: boolean; opportunity_inbox: boolean; performance_met: boolean;
  ai_workspace_ready: boolean; acceptance_passed: boolean; gaps?: string[]; evaluated_at: string;
};

export type CommandCenterDashboard = {
  prd_id: string; tenant_id: string; version: string; depends_on: string[];
  business_objectives: number; workspace_questions: number; layout_regions: number;
  opportunity_inbox_items: number; executive_cards: number; ai_workspace_sections: number;
  quick_actions: number; live_update_channels: number; performance_targets: number;
  api_specifications: number; database_tables: number; ai_capabilities: number;
  testing_requirements: number; acceptance_criteria: number; generated_at: string;
};

export type DGCCPRDStatus = {
  prd_id: string; version: string; depends_on: string[];
  business_objectives: number; workspace_questions: number; layout_regions: number;
  opportunity_inbox_items: number; executive_cards: number; ai_workspace_sections: number;
  quick_actions: number; live_update_channels: number; performance_targets: number;
  api_specifications: number; database_tables: number; ai_capabilities: number;
  testing_requirements: number; acceptance_criteria: number;
  default_landing: boolean; production_ready: boolean; status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const dailyGrowthCommandCenterApi = {
  dashboard: () => fetchMarketIntel<CommandCenterDashboard>('/dgcc/dashboard'),
  businessObjectives: () => fetchMarketIntel<{ objectives: BusinessObjective[] }>('/dgcc/business-objectives').then((r) => r.objectives),
  workspacePhilosophy: () => fetchMarketIntel<{ philosophy: WorkspacePhilosophyQuestion[] }>('/dgcc/workspace-philosophy').then((r) => r.philosophy),
  layout: () => fetchMarketIntel<{ layout: LayoutRegion[] }>('/dgcc/layout').then((r) => r.layout),
  opportunityInbox: () => fetchMarketIntel<{ opportunities: OpportunityInboxItem[] }>('/dgcc/opportunity-inbox').then((r) => r.opportunities),
  executiveCards: () => fetchMarketIntel<{ cards: ExecutiveCard[] }>('/dgcc/executive-cards').then((r) => r.cards),
  aiWorkspace: () => fetchMarketIntel<{ sections: AIWorkspaceSection[] }>('/dgcc/ai-workspace').then((r) => r.sections),
  quickActions: () => fetchMarketIntel<{ actions: QuickAction[] }>('/dgcc/quick-actions').then((r) => r.actions),
  liveUpdates: () => fetchMarketIntel<{ channels: LiveUpdateChannel[] }>('/dgcc/live-updates').then((r) => r.channels),
  performanceTargets: () => fetchMarketIntel<{ targets: PerformanceTarget[] }>('/dgcc/performance-targets').then((r) => r.targets),
  apis: () => fetchMarketIntel<{ apis: APISpecification[] }>('/dgcc/apis').then((r) => r.apis),
  databaseSchema: () => fetchMarketIntel<{ tables: DatabaseTable[] }>('/dgcc/database-schema').then((r) => r.tables),
  aiCapabilities: () => fetchMarketIntel<{ capabilities: AICapability[] }>('/dgcc/ai-capabilities').then((r) => r.capabilities),
  testing: () => fetchMarketIntel<{ requirements: TestingRequirement[] }>('/dgcc/testing').then((r) => r.requirements),
  acceptanceCriteria: () => fetchMarketIntel<{ criteria: AcceptanceCriterion[] }>('/dgcc/acceptance-criteria').then((r) => r.criteria),
  validate: (payload: { attrs: Record<string, boolean> }) =>
    fetchMarketIntel<CommandCenterValidation>('/dgcc/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchMarketIntel<DGCCPRDStatus>('/dgcc/status'),
};
